import {
  CIBUS_CODE_PREFIX,
  TENBIS_CODE_PREFIX,
  WOLT_CODE_PREFIX,
  isAggregatorOrderCode,
  resolveCibusPhase,
  resolveTenbisPhase,
  resolveWoltPhase,
  takeAwayChannelLabel,
  type CibusOrderPhase,
  type OrderView,
  type TenbisOrderPhase,
  type WoltOrderPhase,
} from 'shared-models/browser';

export type AggregatorOrderSnapshot = Pick<OrderView, 'orderCode' | 'status' | 'woltStatus' | 'tenbisStatus' | 'cibusStatus' | 'createdAt'>;
type AggregatorOrderPhase = WoltOrderPhase | TenbisOrderPhase | CibusOrderPhase;

export type ChannelAlertTone = 'blue' | 'green' | 'coral';

const ALERT_TONE_BY_PREFIX: Array<[string, ChannelAlertTone]> = [
  [WOLT_CODE_PREFIX, 'blue'],
  [TENBIS_CODE_PREFIX, 'green'],
  [CIBUS_CODE_PREFIX, 'coral'],
];

// Per-channel phase resolution keyed by order-code prefix; a new channel adds its entry here.
const PHASE_RESOLVER_BY_PREFIX: Array<[string, (order: AggregatorOrderSnapshot) => AggregatorOrderPhase]> = [
  [WOLT_CODE_PREFIX, (order) => resolveWoltPhase(order.woltStatus)],
  [TENBIS_CODE_PREFIX, (order) => resolveTenbisPhase(order.tenbisStatus)],
  [CIBUS_CODE_PREFIX, (order) => resolveCibusPhase(order.cibusStatus)],
];

// Phases that still owe the manager a decision: NEW awaits קבל/דחה, ACCEPTED awaits מוכן.
const ACTION_PENDING_PHASES: AggregatorOrderPhase[] = ['new', 'accepted'];

// Channel-side refusal leaves the local order open, so it keeps glowing until a manager voids it.
const OPEN_ORDER_ACTION_PHASES: AggregatorOrderPhase[] = ['rejected', 'canceled'];

export function aggregatorOrderPhase(order: AggregatorOrderSnapshot): AggregatorOrderPhase {
  const resolver = PHASE_RESOLVER_BY_PREFIX.find(([prefix]) => order.orderCode?.startsWith(prefix))?.[1];
  return resolver ? resolver(order) : 'unknown';
}

export function aggregatorOrderNeedsAction(order: AggregatorOrderSnapshot): boolean {
  if (!isAggregatorOrderCode(order.orderCode)) {
    return false;
  }
  const phase = aggregatorOrderPhase(order);
  return ACTION_PENDING_PHASES.includes(phase)
    || (order.status === 'open' && OPEN_ORDER_ACTION_PHASES.includes(phase));
}

// Glow colour of a channel order code (or bare prefix); null when no channel owns it.
export function channelAlertTone(orderCode: string | null | undefined): ChannelAlertTone | null {
  return ALERT_TONE_BY_PREFIX.find(([prefix]) => orderCode?.startsWith(prefix))?.[1] ?? null;
}

export function aggregatorOrderAlertTone(order: AggregatorOrderSnapshot): ChannelAlertTone | null {
  return aggregatorOrderNeedsAction(order) ? channelAlertTone(order.orderCode) : null;
}

// Prefix of the NEWEST order still owing action, so a mixed tab glows for the latest channel.
export function hasAggregatorOrderNeedingAction(orders: AggregatorOrderSnapshot[]): string | null {
  const newest = orders
    .filter(aggregatorOrderNeedsAction)
    .reduce<AggregatorOrderSnapshot | null>(
      (best, order) => (!best || order.createdAt > best.createdAt ? order : best),
      null,
    );
  return ALERT_TONE_BY_PREFIX.find(([prefix]) => newest?.orderCode?.startsWith(prefix))?.[0] ?? null;
}

// Channel name for the order's panel/modal copy; never hard-code one aggregator's brand.
export function aggregatorChannelLabel(orderCode: string): string {
  return takeAwayChannelLabel(orderCode) ?? 'משלוח';
}
